import React, { useContext, useMemo, useState, useCallback } from "react";
import moment from "moment";
import _ from "lodash";
import styled, { useTheme } from "styled-components";
import { Flex } from "reflexbox";
import { useQuery } from "../Hooks";
import { PageWrapper } from "../UI/Grid";
import { Body } from "../UI/Text";
import Table from "../UI/Table";
import Modal from "../UI/Modal";
import Button from "../UI/Button";
import Card from "../UI/Card";
import AlertContext from "../../context/alert/AlertContext";
import InteractionForm from "./Form";
import View from "./View";
import * as interactionRequests from "../../requests/interactionsRequests";
import { getInteractionTopics } from "../../requests/interactionTopicRequests";
import UserContext from "../../context/user/UserContext";
import { useParams } from "react-router-dom";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";

const { getInteractions, deleteInteraction } = interactionRequests;

const ButtonsWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  transition: 200ms all ease-out;
`;

export const InteractionsTable = ({ groupId, quoteId }) => {
  const theme = useTheme();
  const { username, quoteId: paramQuoteId } = useParams();
  const { showSuccessAlert } = useContext(AlertContext);
  const { quote } = useContext(ConfiguratorContext);
  const userContext = useContext(UserContext);

  const firstName = useMemo(
    () => userContext?.activeUser?.firstName,
    [userContext]
  );
  const lastName = useMemo(
    () => userContext?.activeUser?.lastName,
    [userContext]
  );
  const roleLevel = useMemo(
    () => userContext?.activeUser?.roleLevel,
    [userContext]
  );

  const [show, setShow] = useState(false);
  const [interactionId, setInteractionId] = useState("create");

  const { data: interactionTopics, isLoading: topicsLoading } = useQuery({
    queryID: "interactionTopics",
    request: getInteractionTopics,
    skip: userContext.isLoading,
  });

  const { data, isLoading, refetchQuery, setData } = useQuery({
    queryID: "interactionsPage",
    request: getInteractions,
    queryVariables: {
      groupId,
      quoteId,
      erpOrderNo: paramQuoteId ? quote?.erpOrderNo : undefined,
      ...(!!username ? { firstName, lastName } : {}),
    },
    skip:
      (quoteId && !quote) ||
      !interactionTopics ||
      (!interactionTopics && topicsLoading),
  });
  //not tied to an order has not ref_ttype

  const { count = 0, rows: interactions = [] } = data || {};

  const currentInteraction = useMemo(
    () => _.find(interactions, (__) => __.interaction_id === interactionId),
    [interactions, interactionId]
  );
  const onDelete = useCallback(
    async (e, interaction_id) => {
      e.stopPropagation();
      const { rows } = data;
      const newRows = [...rows];
      _.remove(newRows, (row) => row.interaction_id === interaction_id);
      setData({ rows: newRows, count: count - 1 });
      setShow(false);
      await deleteInteraction({ interaction_id });

      showSuccessAlert(
        `Successfully deleted Interaction with the ID of ${interaction_id}`
      );
      refetchQuery();
    },
    [count, data, setData, showSuccessAlert, refetchQuery]
  );

  const onRowClick = (row) => {
    setInteractionId(row.original.interaction_id);
    setShow(true);
  };

  const getRefType = useCallback((type) => {
    switch (type) {
      case "C":
        return "Customer Order";
      default:
        return "—";
    }
  }, []);
  const getStatus = useCallback((stat) => {
    switch (stat) {
      case "C":
        return "Complete";
      case "A":
        return "Active";
      default:
        return "—";
    }
  }, []);

  const columns = useMemo(() => {
    let cols = [
      {
        id: "interaction_id",
        data: (d) => (
          <Body center>{`${d?.interaction_id}-${d?.site_ref}`}</Body>
        ),
        header: "Interaction",
        size: { md: 1, sm: 0, xs: 0 },
      },
      {
        id: "RefType",
        data: (d) => <Body center>{getRefType(d?.RefType) || "—"}</Body>,
        header: "Reference",
        size: { md: 1, sm: 0, xs: 0 },
      },
      {
        id: "ref_num_id",
        data: (d) => <Body center>{d?.ref_num_id || "—"}</Body>,
        header: "Reference #",
        size: { md: 1, sm: 0, xs: 0 },
      },
      {
        id: "stat",
        data: (d) => (
          <Body
            bold
            color={d?.stat === "A" ? theme.secondary : theme.success}
            center
          >
            {getStatus(d?.stat) || "—"}
          </Body>
        ),
        header: "Status",
        size: { md: 1, sm: 0, xs: 0 },
      },
      {
        id: "CreateDate",
        data: (d) => (
          <Body center ellipsis>
            {moment(d?.CreateDate).format("MM/DD/YYYY") || "—"}
          </Body>
        ),
        header: "Created",
        size: { md: 1, sm: 1, xs: 1 },
      },
      {
        id: "InteractionDate",
        data: (d) => (
          <Body center ellipsis>
            {moment(d?.InteractionDate).format("MM/DD/YYYY") || "—"}
          </Body>
        ),
        header: "Interaction Date",
        size: { md: 0, sm: 0, xs: 0 },
      },
      {
        id: "description",
        data: (d) => <Body>{d?.description || "—"}</Body>,
        header: "Description",
        size: { md: 0, sm: 0, xs: 0 },
      },
      {
        id: "CreatedBy",
        data: (d) => <Body>{d?.CreatedBy || "—"}</Body>,
        header: "Started By",
        size: { md: 0, sm: 0, xs: 0 },
      },
    ];
    if (roleLevel === 9)
      cols.push({
        display: (props) => {
          let interaction = props.cell.row.original;

          return (
            <ButtonsWrapper id="actions">
              <Button
                size="small"
                color={theme.secondary}
                reverse
                iconName="faPencil"
                onClick={(e) => onRowClick(props.cell.row, e)}
              />
              <Button
                size="small"
                color={theme.danger}
                reverse
                iconName="faTrashCan"
                onClick={(e) => onDelete(e, interaction.interaction_id)}
              />
            </ButtonsWrapper>
          );
        },
        header: "",
        sort: false,
        size: { lg: 1.5, md: 2, sm: 0, xs: 0 },
      });
    return cols;
  }, [
    roleLevel,
    getRefType,
    theme.secondary,
    theme.danger,
    theme.success,
    getStatus,
    onDelete,
  ]);

  const modalComponent = useMemo(() => {
    return interactionId === "create" ? (
      <InteractionForm
        interactionTopics={interactionTopics}
        data={data}
        count={count}
        interactionId={interactionId}
        setInteractionId={setInteractionId}
        currentInteraction={currentInteraction}
        setShow={setShow}
        setData={setData}
        refetchQuery={refetchQuery}
      />
    ) : (
      <View
        interactionId={interactionId}
        setInteractionId={setInteractionId}
        currentInteraction={currentInteraction}
      />
    );
  }, [
    count,
    currentInteraction,
    data,
    interactionId,
    interactionTopics,
    setData,
    refetchQuery,
  ]);

  return (
    <>
      <Modal show={show} setShow={setShow}>
        <Card>{modalComponent}</Card>
      </Modal>
      <Table
        title={`${!!username ? "My " : ""}Interactions`}
        data={interactions}
        createButtonText="Create Interaction"
        createLink={() => {
          setInteractionId("create");
          setShow(true);
        }}
        createButtonIcon="faComment"
        hideToggle
        hideRefetch={false}
        refetchData={refetchQuery}
        // onRowClick={onRowClick}
        columns={columns}
        isLoading={isLoading}
        totalCount={count}
        backButtonIcon={paramQuoteId ? "faXmark" : undefined}
        backButtonColor={paramQuoteId ? theme.danger : undefined}
      />
    </>
  );
};

export const InteractionsPage = (props) => {
  return (
    <PageWrapper>
      <InteractionsTable {...props} />
    </PageWrapper>
  );
};

export default InteractionsPage;
